import { skipToken } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { AlertCircle } from "lucide-react"
import { EstateRelationshipTree } from "@/components/estate-relationship-tree"
import {
  InventClientSelector,
  InventClientSyncFailureNotice,
  useInventClientSelection,
  useInventClientSync,
  useInventEmbedConnection,
} from "@/components/invent/invent-embed"
import {
  isEmptyWillReport,
  isFailedWillReport,
  useWillReportGeneration,
} from "@/components/reports/use-will-report-generation"
import { WillReportContent } from "@/components/reports/will-report-content"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Typography } from "@/components/ui/typography"
import { trpc } from "@/lib/trpc"

export const Route = createFileRoute("/integrations/invent/embed/will-report")({
  component: EmbeddedWillReport,
})

function CentreState({ children }: { children: React.ReactNode }) {
  return <main className="flex min-h-screen items-center justify-center p-6">{children}</main>
}

function EmbeddedWillReport() {
  const { connectionState } = useInventEmbedConnection()
  const {
    data: user,
    isLoading: isUserLoading,
    isError: isUserError,
  } = trpc.users.me.useQuery(undefined, { enabled: connectionState === "connected" })
  const isProfessional = user?.userType === "professional"
  const { mutateAsync: syncClients } = trpc.partnerSync.syncClients.useMutation()
  const shouldSyncClients =
    connectionState === "connected" && !isUserLoading && !isUserError && isProfessional
  const clientSync = useInventClientSync({ enabled: shouldSyncClients, syncClients })
  const canLoadAdvisorData = shouldSyncClients && clientSync.isFinished
  const {
    data: estates = [],
    isLoading: areEstatesLoading,
    isError: isEstatesError,
  } = trpc.estates.list.useQuery(undefined, {
    enabled: canLoadAdvisorData,
  })
  const { eligibleEstates, selectedEstateId, setSelectedEstateId } =
    useInventClientSelection(estates)
  const canLoadReport =
    canLoadAdvisorData && !areEstatesLoading && !isEstatesError && Boolean(selectedEstateId)
  const reportInput = canLoadReport
    ? { estateId: selectedEstateId, reportTypes: ["WILL_REVIEW"] as ["WILL_REVIEW"] }
    : skipToken
  const {
    data: reports = [],
    isLoading: areReportsLoading,
    isError: isReportsError,
    refetch: refetchReports,
  } = trpc.reports.getOfType.useQuery(reportInput)
  const {
    data: infographicData,
    isLoading: isInfographicLoading,
    isError: isInfographicError,
    refetch: refetchInfographic,
  } = trpc.reports.getWillInfographic.useQuery(
    canLoadReport ? { estateId: selectedEstateId } : skipToken,
  )
  const generateReport = trpc.reports.generate.useMutation()
  const generation = useWillReportGeneration({
    estateId: selectedEstateId,
    reports,
    generate: generateReport.mutateAsync,
    refetchReports,
    refreshInfographic: refetchInfographic,
  })
  const latestReport = reports[0] ?? null
  const hasPersistedGenerationFailure = isFailedWillReport(latestReport)
  const completedReport =
    latestReport && !isEmptyWillReport(latestReport) && !hasPersistedGenerationFailure
      ? latestReport
      : null

  if (connectionState === "connecting") {
    return (
      <CentreState>
        <output>Connecting to INVENT...</output>
      </CentreState>
    )
  }

  if (connectionState === "error") {
    return (
      <CentreState>
        <p role="alert">Unable to connect to INVENT. Reload this page from the INVENT portal.</p>
      </CentreState>
    )
  }

  if (isUserLoading) {
    return (
      <CentreState>
        <output>Loading Will report...</output>
      </CentreState>
    )
  }

  if (isUserError || !user) {
    return (
      <CentreState>
        <p role="alert">Unable to load your Legacy Keeper profile.</p>
      </CentreState>
    )
  }

  if (!isProfessional) {
    return (
      <CentreState>
        <p role="alert">This report is available to INVENT advisors only.</p>
      </CentreState>
    )
  }

  if (!clientSync.isFinished) {
    return (
      <CentreState>
        <output>Syncing your clients from INVENT...</output>
      </CentreState>
    )
  }

  if (areEstatesLoading) {
    return (
      <CentreState>
        <output>Loading clients...</output>
      </CentreState>
    )
  }

  if (isEstatesError) {
    return (
      <CentreState>
        <p role="alert">Unable to load clients.</p>
      </CentreState>
    )
  }

  if (!selectedEstateId) {
    return (
      <CentreState>
        <div className="space-y-2 text-center">
          {clientSync.state === "failed" && <InventClientSyncFailureNotice />}
          <p>No clients are available for this Will report.</p>
        </div>
      </CentreState>
    )
  }

  if (isReportsError || isInfographicError) {
    return (
      <CentreState>
        <p role="alert">Unable to load the Will report.</p>
      </CentreState>
    )
  }

  return (
    <main className="min-h-screen p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="space-y-2">
          {clientSync.state === "failed" && <InventClientSyncFailureNotice />}
          <InventClientSelector
            estates={eligibleEstates}
            selectedEstateId={selectedEstateId}
            onChange={setSelectedEstateId}
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold">Will report</h1>
            <p className="text-sm text-muted-foreground">
              Review the selected client's Will analysis and recommendations.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => void generation.startGeneration()}
            disabled={areReportsLoading || generation.isActive || generation.isPassiveProcessing}
          >
            Run report
          </Button>
        </div>

        {generation.message && (
          <div role={generation.status === "failed" ? "alert" : "status"} className="space-y-2">
            <p>{generation.message}</p>
            {generation.status === "delayed" && (
              <Button
                type="button"
                variant="outline"
                onClick={() => void generation.manualRefresh()}
              >
                Refresh report
              </Button>
            )}
          </div>
        )}

        {hasPersistedGenerationFailure && generation.status === "idle" && (
          <p role="alert">Report generation failed. Try again when you are ready.</p>
        )}

        {generation.isPassiveProcessing && (
          <output className="block space-y-2">
            <p>A Will report is processing. Refresh manually to check for new results.</p>
            <Button type="button" variant="outline" onClick={() => void generation.manualRefresh()}>
              Refresh report
            </Button>
          </output>
        )}

        {(areReportsLoading || isInfographicLoading) && <output>Loading report data...</output>}

        {!areReportsLoading && !latestReport && !generation.isPassiveProcessing && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 text-yellow-500" />
                No Will report generated
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Typography as="p" className="text-muted-foreground">
                Run the Will report to view analysis and recommendations for this client.
              </Typography>
            </CardContent>
          </Card>
        )}

        <EstateRelationshipTree estateId={selectedEstateId} showPdfDownload={false} />
        <WillReportContent report={completedReport} infographicData={infographicData} />
      </div>
    </main>
  )
}
